import React, {useState} from 'react';

import {Button, Icon, Input, Text, addToast, addGenericErrorToast, Banner} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';

import OnboardingPageLayout from '../OnboardingPageLayout';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface NewParentalConsentStepProps {
  onBack: () => void;
  onSubmit: (email: string) => void;
  /** Pre-fills the input when the student has already submitted a parental email. */
  defaultEmail?: string;
  /** Non-empty when the student has already submitted a parental-consent email. */
  pendingParentEmail?: string;
  /** Manually re-checks whether the parent has approved (refetches the user query). */
  onCheckStatus?: () => void;
}

export default function NewParentalConsentStep({
  onBack,
  onSubmit,
  defaultEmail = '',
  pendingParentEmail,
  onCheckStatus
}: NewParentalConsentStepProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const trimmedEmail = email.trim();
  const formatError =
    touched && trimmedEmail && !EMAIL_REGEX.test(trimmedEmail)
      ? 'Please enter a valid email address.'
      : null;
  const displayError = error || formatError;
  const submitLabel = defaultEmail ? 'Resend' : 'Submit';

  const handleSubmit = async () => {
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const userId = sessionStore.getUserId();

      await genericMandarkRequest(
        'patch',
        {resourcePath: ['users_v2', userId]},
        {
          data: {
            type: 'users_v2',
            id: userId,
            attributes: {coppa_consent_email: trimmedEmail}
          }
        }
      );

      invalidatePartialInterest({
        resourcePath: ['users_v2', userId, 'coppa_consent_grants_v1']
      });

      addToast({
        title: 'Email sent!',
        color: 'positive'
      });
      onSubmit(trimmedEmail);
    } catch (e: any) {
      const detail = e?.response?.body?.errors?.[0]?.detail;
      if (detail) {
        setError(detail);
      } else {
        addGenericErrorToast();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OnboardingPageLayout>
      <Button
        color='primary'
        variant='text'
        size='s'
        onClick={onBack}
        className='flex max-w-fit items-center justify-start gap-x1'
      >
        <Icon icon='arrow-left' />
        <Text color='inherit'>Back</Text>
      </Button>

      {pendingParentEmail && (
        <Banner type='info'>
          <div className='flex flex-1 flex-col gap-x0-half'>
            <Text size='s' color='inherit'>
              We&apos;ve sent an email to <strong>{pendingParentEmail}</strong>. Once your parent
              approves, refresh the page and you&apos;ll be able to continue.
            </Text>
          </div>
          {onCheckStatus && (
            <Button color='primary' variant='text' size='s' onClick={onCheckStatus}>
              <Text color='inherit' size='s'>
                Check Status
              </Text>
            </Button>
          )}
        </Banner>
      )}

      <div className='flex flex-col gap-x0-half'>
        <Text className='text-xxl font-black'>Request Parental Permission</Text>
        <Text>
          Tell us your parent&apos;s email and we&apos;ll ask them if it&apos;s okay for you to use
          Albert.
        </Text>
      </div>

      <div className='px-x1'>
        <Input
          type='email'
          placeholder='Enter email'
          value={email}
          error={!!displayError}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          onBlur={() => setTouched(true)}
          aria-label='Parent email'
        />
        {displayError && (
          <Text size='s' color='negative' className='mt-x0-half'>
            {displayError}
          </Text>
        )}
      </div>

      <Button
        className='w-full'
        color='primary'
        size='l'
        roundness='pill'
        disabled={!trimmedEmail || !!displayError || isSubmitting}
        onClick={handleSubmit}
      >
        <Text color='primary-inverse-static'>{isSubmitting ? 'Sending...' : submitLabel}</Text>
      </Button>
    </OnboardingPageLayout>
  );
}
